import { useEffect, useRef, useState } from "react";
import {
  createPaymentIn,
  createPaymentOut,
  getAllPurchase,
  getAllSale,
  searchParties,
} from "../../services/services";
import { toastError, toastSuccess } from "../../utils";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarInput from "./CalendarInput";
import AsyncSearchSelect from "../sale/components/AsyncSearchSelect";

const PaymentModal = ({
  party,
  selectedInvoiceId,
  onClose,
  onPaymentSuccess,
  paymentType: initialPaymentType,
}) => {
  const [paymentFor, setPaymentFor] = useState("customer");

  const [selectedParty, setSelectedParty] = useState(party || null);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const LIMIT = 10;

  const [paymentType, setPaymentType] = useState(
    initialPaymentType || "payment-in",
  );

  useEffect(() => {
    if (selectedParty?.partyType === "both") {
      if (paymentType === "payment-in") {
        setPaymentFor("customer");
      } else {
        setPaymentFor("vendor");
      }
    }

    if (selectedParty?.partyType === "customer") {
      setPaymentFor("customer");
    }

    if (selectedParty?.partyType === "vendor") {
      setPaymentFor("vendor");
    }
  }, [paymentType, selectedParty]);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    partyName: party?.name || "",
    mobile: party?.mobile || "",
    amount: "",
    paymentMode: "cash",
    invoiceId: selectedInvoiceId || "",
  });
  const [errors, setErrors] = useState({});
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [parties, setParties] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const hasMore = page < totalPages;

  const loadParties = async (pageNumber = 1, searchText = "") => {
    try {
      setLoading(true);

      const res = await searchParties({
        page: pageNumber,
        limit: LIMIT,
        search: searchText,
      });

      if (res?.data?.status) {
        const list = res.data.response || [];

        if (pageNumber === 1) {
          setParties(list);
        } else {
          setParties((prev) => [...prev, ...list]);
        }

        setTotalPages(res.data.response.totalPages);
        setPage(pageNumber);
      }
    } catch (err) {
      console.error("Party fetch error:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      loadParties(1, search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadParties(1, "");
  }, []);

  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const overlay = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (overlay) {
          overlay.style.display = "none";
        }
      }
    };

    window.addEventListener("error", resizeObserverError);
    return () => window.removeEventListener("error", resizeObserverError);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      loadParties(page + 1, search);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!selectedParty?._id || !/^[0-9a-fA-F]{24}$/.test(selectedParty._id)) {
      newErrors.party = "Please select a valid party";
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePaymentTypeChange = (e) => {
    setPaymentType(e.target.value);
    setPendingInvoices([]); // clear old data
    setFormData((prev) => ({ ...prev, invoiceId: "" }));
  };

  useEffect(() => {
    setErrors(validateForm());
  }, [formData, selectedParty, paymentType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      party: selectedParty._id,
      partyName: formData.partyName,
      mobile: formData.mobile,
      amount: parseFloat(formData.amount),
      date: new Date(formData.date).toISOString(),
      paymentMode: formData.paymentMode,

      ...(paymentType === "payment-in"
        ? { sale: formData.invoiceId }
        : { purchase: formData.invoiceId }),

      ...(selectedParty?.partyType === "both" && {
        paymentFor,
      }),
    };
    setSaving(true);
    try {
      const response =
        paymentType === "payment-in"
          ? await createPaymentIn(payload)
          : await createPaymentOut(payload);

      toastSuccess(response.data.message);
      onPaymentSuccess(response);
      onClose();
    } catch (error) {
      console.error(
        `Error creating ${paymentType}:`,
        error.response?.data || error,
      );
      toastError(
        error.response?.data?.error || `Failed to create ${paymentType}`,
      );
    } finally {
      setSaving(false);
    }
  };

  const fetchPendingSales = async () => {
    if (!selectedParty?._id) return;
    const payload = {
      query: {
        customerId: selectedParty._id,
        status: "unpaid",
        dueBalance: true,
      },
    };
    try {
      const res = await getAllSale(payload);

      if (res.data.status) {
        setPendingInvoices(res.data.response.sales);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPendingPurchases = async () => {
    if (!selectedParty?._id) return;
    const payload = {
      query: {
        vendor: selectedParty._id,
        status: "unpaid",
        dueBalance: true,
      },
    };
    try {
      const res = await getAllPurchase(payload);

      if (res.data.status) {
        setPendingInvoices(res.data.response.purchases);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!selectedParty?._id) return;

    if (selectedParty.partyType === "customer") {
      fetchPendingSales();
    } else if (selectedParty.partyType === "vendor") {
      fetchPendingPurchases();
    } else if (selectedParty.partyType === "both") {
      if (paymentType === "payment-in") {
        paymentFor === "customer"
          ? fetchPendingSales()
          : setPendingInvoices([]); // vendor refund has no sale
      } else {
        paymentFor === "vendor"
          ? fetchPendingPurchases()
          : setPendingInvoices([]); // customer refund no purchase
      }
    }
  }, [selectedParty, paymentType, paymentFor]);

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-custom text-white py-2">
            <h5 className="modal-title mb-0">
              {/* {paymentType === "payment-in" ? "Payment In" : "Payment Out"} */}
              Payments
            </h5>
            <div className="fs-3" onClick={onClose}>
              <i className="bi bi-x"></i>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                {/* Payment Type */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="paymentType" className="form-label">
                    Payment Type <span className="text-danger ms-1">*</span>
                  </label>
                  <select
                    id="paymentType"
                    name="paymentType"
                    value={paymentType}
                    onChange={handlePaymentTypeChange}
                    className={`form-select ${
                      errors.paymentType ? "is-invalid" : ""
                    }`}
                    required
                  >
                    <option value="payment-in">Payment In</option>
                    <option value="payment-out">Payment Out</option>
                  </select>
                  {errors.paymentType && (
                    <div className="invalid-feedback">{errors.paymentType}</div>
                  )}
                </div>

                {selectedParty?.partyType === "both" && (
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Payment For <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={paymentFor}
                      onChange={(e) => setPaymentFor(e.target.value)}
                    >
                      <option value="customer">Customer</option>
                      <option value="vendor">Vendor</option>
                    </select>
                  </div>
                )}

                {/* Date */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Date</label>
                  <DatePicker
                    selected={formData.date ? new Date(formData.date) : null}
                    onChange={(date) =>
                      setFormData((prev) => ({ ...prev, date }))
                    }
                    customInput={<CalendarInput />}
                    dateFormat="dd-MM-yyyy"
                  />
                </div>
                <div
                  className="col-md-6 mb-3 position-relative"
                  ref={wrapperRef}
                >
                  <label className="form-label">
                    Select Party <span className="text-danger ms-1">*</span>
                  </label>

                  <AsyncSearchSelect
                    value={
                      selectedParty
                        ? {
                            label: `${selectedParty.name} (${selectedParty.partyType})`,
                            value: selectedParty._id,
                            data: selectedParty,
                          }
                        : null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedParty(option.data);
                        setFormData((prev) => ({
                          ...prev,
                          partyName: option.data.name,
                          mobile: option.data.mobile,
                        }));
                      } else {
                        setSelectedParty(null);
                        setFormData((prev) => ({
                          ...prev,
                          partyName: "",
                          mobile: "",
                        }));
                      }
                    }}
                    loadOptions={async (input) => {
                      const res = await searchParties({
                        page: 1,
                        limit: LIMIT,
                        search: input || "",
                      });

                      const list = res?.data?.response || [];

                      return list.map((p) => ({
                        label: `${p.name} (${p.partyType})`,
                        value: p._id,
                        data: p,
                      }));
                    }}
                    placeholder="Search Party..."
                    minInputLength={2}
                    debounceWait={400}
                    isClearable
                    menuPortalTarget={document.body}
                  />

                  {errors.party && (
                    <div className="invalid-feedback d-block">
                      {errors.party}
                    </div>
                  )}
                </div>
                {/* <div className="position-relative">
                    <input
                      type="text"
                      className={`form-control pe-5 ${errors.party ? "is-invalid" : ""}`}
                      placeholder="Search party..."
                      value={search !== "" ? search : selectedParty?.name || ""}
                      onFocus={() => {
                        setIsFocused(true);
                        setShowDropdown(true);
                      }}
                      onBlur={() => setIsFocused(false)}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setSelectedParty(null);
                      }}
                    />

                    <button
                      type="button"
                      className="position-absolute top-50 end-0 translate-middle-y me-2 btn btn-sm "
                      onClick={() => {
                        if (isFocused && (search || selectedParty)) {
                          setSearch("");
                          setSelectedParty(null);
                          dispatch(
                            fetchParties({
                              page: 1,
                              limit: LIMIT,
                              search: "",
                            }),
                          );
                          setShowDropdown(true);
                        } else {
                          setShowDropdown((prev) => !prev);
                        }
                      }}
                    >
                      {isFocused && (search || selectedParty) ? (
                        "✕"
                      ) : showDropdown ? (
                        <i className="bi bi-chevron-up"></i>
                      ) : (
                        <i className="bi bi-chevron-down"></i>
                      )}
                    </button>
                  </div>
 
                  {showDropdown && (
                    <div
                      ref={dropdownRef}
                      onScroll={handleScroll}
                      className="position-absolute w-100 bg-white border rounded shadow"
                      style={{
                        maxHeight: "220px",
                        overflowY: "auto",
                        zIndex: 1000,
                      }}
                    >
                      {parties.length === 0 ? (
                        <div className="p-2 text-muted">No parties found</div>
                      ) : (
                        parties.map((p) => (
                          <div
                            key={p._id}
                            className="p-2 dropdown-item cursor-pointer"
                            onClick={() => {
                              setSelectedParty(p);
                              setSearch("");
                              setShowDropdown(false);
                            }}
                          >
                            <strong>{p.name}</strong>
                            <span className="text-muted small">
                              (₹ {(p.balance || 0).toFixed(2)})
                            </span>
                          </div>
                        ))
                      )}

                      {hasMore && (
                        <div className="p-2 text-center text-muted small">
                          Loading more...
                        </div>
                      )}
                    </div>
                  )} */}

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    {paymentType === "payment-in"
                      ? paymentFor === "customer"
                        ? "Pending Sales"
                        : "Vendor Refund (No Invoice)"
                      : paymentFor === "vendor"
                        ? "Pending Purchases"
                        : "Customer Refund (No Invoice)"}
                  </label>
                  <select
                    id="sale"
                    name="invoiceId"
                    value={formData.invoiceId}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="" disabled>
                      Select Invoice
                    </option>
                    {pendingInvoices.length == 0 ? (
                      <option disabled value="">
                        No pending invoice
                      </option>
                    ) : (
                      <>
                        {pendingInvoices.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.invoiceNumber} - Due ₹
                            {(p.dueAmount || 0).toFixed(2)}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Mobile */}
                {/* <div className="col-md-6 mb-3">
                  <label htmlFor="mobile" className="form-label">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className={`form-control ${
                      errors.mobile ? "is-invalid" : ""
                    }`}
                    placeholder="Enter 10-digit mobile number"
                  />
                </div> */}

                {/* Amount */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="amount" className="form-label">
                    {paymentType === "payment-in"
                      ? "Received Amount (₹)"
                      : "Paid Amount (₹)"}{" "}
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder={`Enter ${
                      paymentType === "payment-in" ? "received" : "paid"
                    } amount`}
                    className={`form-control ${
                      errors.amount ? "is-invalid" : ""
                    }`}
                    required
                  />
                  {errors.amount && (
                    <div className="invalid-feedback">{errors.amount}</div>
                  )}
                </div>

                {/* Payment Mode */}
                <div className="col-md-6 mb-3">
                  <label htmlFor="paymentMode" className="form-label">
                    Payment Mode
                  </label>
                  <select
                    id="paymentMode"
                    name="paymentMode"
                    value={formData.paymentMode}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="card">Card</option>
                    <option value="online">Online</option>
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="text-end">
                <button
                  type="button"
                  className="btn btn-custom me-2"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-custom"
                  disabled={Object.keys(errors).length > 0 || saving}
                >
                  {saving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
